import jsPDF from 'jspdf';
import { 
  LabelTemplate, 
  LabelElement, 
  TextElement,
  ShapeElement,
  ImageElement,
  IconElement,
  LabelSheetConfig,
  PRINT_DPI 
} from '@/types/label';
import { generateBarcodeCanvas, generateQRCodeCanvas } from './barcodes';
import { icons as LucideIcons } from 'lucide-react';
import { renderToStaticMarkup } from 'react-dom/server';
import React from 'react';

export const generateLabelPdfBlob = async (
  template: LabelTemplate,
  resolveContent: (content: string) => string
): Promise<Blob> => {
  const labelWidth = template.width;
  const labelHeight = template.height;
  
  const pdf = new jsPDF({
    orientation: labelWidth > labelHeight ? 'landscape' : 'portrait',
    unit: 'mm',
    format: [labelWidth, labelHeight],
  });

  await renderLabelElements(pdf, template, resolveContent, 0, 0);

  return pdf.output('blob');
};

export const exportLabelPdf = async (
  template: LabelTemplate,
  resolveContent: (content: string) => string
): Promise<void> => {
  const labelWidth = template.width;
  const labelHeight = template.height;
  
  const pdf = new jsPDF({
    orientation: labelWidth > labelHeight ? 'landscape' : 'portrait',
    unit: 'mm',
    format: [labelWidth, labelHeight],
  });

  await renderLabelElements(pdf, template, resolveContent, 0, 0);

  pdf.save(`label-${Date.now()}.pdf`);
};

export const generateMultiLabelPdfBlob = async (
  template: LabelTemplate,
  resolveContent: (content: string, rowData?: Record<string, string>) => string,
  dataRows: Record<string, string>[],
  sheetConfig: LabelSheetConfig
): Promise<Blob> => {
  const pdf = new jsPDF({
    orientation: sheetConfig.pageWidth > sheetConfig.pageHeight ? 'landscape' : 'portrait',
    unit: 'mm',
    format: [sheetConfig.pageWidth, sheetConfig.pageHeight],
  });

  const labelsPerPage = sheetConfig.columns * sheetConfig.rows;
  let labelIndex = 0;

  for (let dataIndex = 0; dataIndex < dataRows.length; dataIndex++) {
    const rowData = dataRows[dataIndex];
    const positionOnPage = labelIndex % labelsPerPage;
    
    if (positionOnPage === 0 && labelIndex > 0) {
      pdf.addPage([sheetConfig.pageWidth, sheetConfig.pageHeight]);
    }

    const row = Math.floor(positionOnPage / sheetConfig.columns);
    const col = positionOnPage % sheetConfig.columns;

    const x = sheetConfig.marginLeft + col * (sheetConfig.labelWidth + sheetConfig.horizontalGap);
    const y = sheetConfig.marginTop + row * (sheetConfig.labelHeight + sheetConfig.verticalGap);

    const rowResolve = (content: string) => resolveContent(content, rowData);
    
    await renderLabelElements(pdf, template, rowResolve, x, y);
    labelIndex++;
  }

  return pdf.output('blob');
};

export const exportMultiLabelPdf = async (
  template: LabelTemplate,
  resolveContent: (content: string, rowData?: Record<string, string>) => string,
  dataRows: Record<string, string>[],
  sheetConfig: LabelSheetConfig
): Promise<void> => {
  const pdf = new jsPDF({
    orientation: sheetConfig.pageWidth > sheetConfig.pageHeight ? 'landscape' : 'portrait',
    unit: 'mm',
    format: [sheetConfig.pageWidth, sheetConfig.pageHeight],
  });

  const labelsPerPage = sheetConfig.columns * sheetConfig.rows;
  let labelIndex = 0;

  for (let dataIndex = 0; dataIndex < dataRows.length; dataIndex++) {
    const rowData = dataRows[dataIndex];
    const positionOnPage = labelIndex % labelsPerPage;
    
    if (positionOnPage === 0 && labelIndex > 0) {
      pdf.addPage([sheetConfig.pageWidth, sheetConfig.pageHeight]);
    }

    const row = Math.floor(positionOnPage / sheetConfig.columns);
    const col = positionOnPage % sheetConfig.columns;

    const x = sheetConfig.marginLeft + col * (sheetConfig.labelWidth + sheetConfig.horizontalGap);
    const y = sheetConfig.marginTop + row * (sheetConfig.labelHeight + sheetConfig.verticalGap);

    const rowResolve = (content: string) => resolveContent(content, rowData);
    
    await renderLabelElements(pdf, template, rowResolve, x, y);
    labelIndex++;
  }

  pdf.save(`labels-batch-${Date.now()}.pdf`);
};

const renderLabelElements = async (
  pdf: jsPDF,
  template: LabelTemplate,
  resolveContent: (content: string) => string,
  offsetX: number,
  offsetY: number
): Promise<void> => {
  // Sort elements by zIndex to maintain proper layering
  const sortedElements = [...template.elements].sort((a, b) => (a.zIndex || 0) - (b.zIndex || 0));
  
  for (const element of sortedElements) {
    if (element.visible === false) continue;
    
    const content = element.content ? resolveContent(element.content) : '';
    const rotation = element.rotation || 0;
    
    // Apply rotation if needed
    if (rotation) {
      pdf.saveGraphicsState();
      const cx = offsetX + element.position.x + element.size.width / 2;
      const cy = offsetY + element.position.y + element.size.height / 2;
      const rad = (rotation * Math.PI) / 180;
      // Translate to center, rotate, translate back
      const cos = Math.cos(rad);
      const sin = Math.sin(rad);
      // jsPDF transform matrix: [a, b, c, d, e, f] where the matrix is [[a,c,e],[b,d,f],[0,0,1]]
      pdf.setCurrentTransformationMatrix(
        new (pdf as any).Matrix(cos, sin, -sin, cos, cx - cos * cx + sin * cy, cy - sin * cx - cos * cy)
      );
    }
    
    if (element.type === 'text') {
      await renderTextElement(pdf, element as TextElement, content, offsetX, offsetY);
    } else if (element.type === 'barcode') {
      await renderBarcodeElement(pdf, element, content, offsetX, offsetY);
    } else if (element.type === 'qrcode') {
      await renderQRCodeElement(pdf, element, content, offsetX, offsetY);
    } else if (element.type === 'shape') {
      await renderShapeElement(pdf, element as ShapeElement, offsetX, offsetY);
    } else if (element.type === 'image') {
      await renderImageElement(pdf, element as ImageElement, offsetX, offsetY);
    } else if (element.type === 'icon') {
      await renderIconElement(pdf, element as IconElement, offsetX, offsetY);
    }
    
    if (rotation) {
      pdf.restoreGraphicsState();
    }
  }
};

const renderTextElement = async (
  pdf: jsPDF, 
  element: TextElement,
  content: string,
  offsetX: number,
  offsetY: number
): Promise<void> => {
  // Use helvetica as default since custom fonts aren't embedded
  const fontFamily = element.fontFamily.toLowerCase().includes('mono') ? 'courier' : 'helvetica';
  const fontStyle = element.fontWeight === 'bold' ? 'bold' : 'normal';
  
  pdf.setFont(fontFamily, fontStyle);
  pdf.setFontSize(element.fontSize);
  pdf.setTextColor(element.color || '#000000');
  
  // Calculate X position based on alignment - matches canvas exactly
  let x = offsetX + element.position.x;
  if (element.textAlign === 'center') {
    x = offsetX + element.position.x + element.size.width / 2;
  } else if (element.textAlign === 'right') {
    x = offsetX + element.position.x + element.size.width;
  }
  
  // Convert font size from pt to mm: 1pt = 0.352778mm
  const fontSizeMm = element.fontSize * 0.352778;
  
  // Vertical centering calculation to match canvas flexbox centering
  // Canvas uses display:flex + align-items:center which centers the text cap-height
  // jsPDF positions text at baseline, so we offset by ~35% of font height above center
  const y = offsetY + element.position.y + (element.size.height / 2) + (fontSizeMm * 0.35);
  
  pdf.text(content, x, y, { 
    align: element.textAlign,
    maxWidth: element.size.width 
  });
};

const renderBarcodeElement = async (
  pdf: jsPDF,
  element: LabelElement,
  content: string,
  offsetX: number,
  offsetY: number
): Promise<void> => {
  try {
    const barcodeElement = element as import('@/types/label').BarcodeElement;
    const canvas = await generateBarcodeCanvas(
      content,
      element.size.width,
      element.size.height,
      PRINT_DPI,
      barcodeElement.includeText ?? true,
      barcodeElement.barScale ?? 2,
      barcodeElement.barcodeType || 'code128',
      barcodeElement.barHeightRatio || 0.8,
      barcodeElement.quietZone ?? 2,
      barcodeElement.foregroundColor,
      barcodeElement.backgroundColor,
      barcodeElement.textSize,
      {
        keyPrefix: barcodeElement.keyPrefix,
        keySuffix: barcodeElement.keySuffix,
        keySeparator: barcodeElement.keySeparator,
        keySeparatorToken: barcodeElement.keySeparatorToken,
      }
    );
    
    const imgData = canvas.toDataURL('image/png');
    pdf.addImage(
      imgData,
      'PNG',
      offsetX + element.position.x,
      offsetY + element.position.y,
      element.size.width,
      element.size.height
    );
  } catch (error) {
    console.error('Failed to render barcode:', error);
  }
};

const renderQRCodeElement = async (
  pdf: jsPDF,
  element: LabelElement,
  content: string,
  offsetX: number,
  offsetY: number
): Promise<void> => {
  try {
    const size = Math.min(element.size.width, element.size.height);
    const qrElement = element as import('@/types/label').QRCodeElement;
    const canvas = await generateQRCodeCanvas(
      content,
      size,
      PRINT_DPI,
      qrElement.foregroundColor,
      qrElement.backgroundColor,
      {
        keyPrefix: qrElement.keyPrefix,
        keySuffix: qrElement.keySuffix,
        keySeparator: qrElement.keySeparator,
        keySeparatorToken: qrElement.keySeparatorToken,
      }
    );
    
    const imgData = canvas.toDataURL('image/png');
    // Center the QR code if the element is not square
    const xOffset = (element.size.width - size) / 2;
    const yOffset = (element.size.height - size) / 2;
    
    pdf.addImage(
      imgData,
      'PNG',
      offsetX + element.position.x + xOffset,
      offsetY + element.position.y + yOffset,
      size,
      size
    );
  } catch (error) {
    console.error('Failed to render QR code:', error);
  }
};

const renderShapeElement = async (
  pdf: jsPDF,
  element: ShapeElement,
  offsetX: number,
  offsetY: number
): Promise<void> => {
  const x = offsetX + element.position.x;
  const y = offsetY + element.position.y;

  // Set fill and stroke
  if (element.fillColor && element.fillColor !== 'transparent') {
    pdf.setFillColor(element.fillColor);
  }
  if (element.strokeColor) {
    pdf.setDrawColor(element.strokeColor);
  }
  pdf.setLineWidth(element.strokeWidth ? element.strokeWidth * 0.264583 : 0.1);

  const style = element.fillColor && element.fillColor !== 'transparent' ? 'FD' : 'S';

  switch (element.shapeType) {
    case 'rectangle':
      if (element.borderRadius && element.borderRadius > 0) {
        pdf.roundedRect(x, y, element.size.width, element.size.height, element.borderRadius * 0.264583, element.borderRadius * 0.264583, style);
      } else {
        pdf.rect(x, y, element.size.width, element.size.height, style);
      }
      break;
    case 'circle':
      const radius = Math.min(element.size.width, element.size.height) / 2;
      pdf.circle(x + radius, y + radius, radius, style);
      break;
    case 'line':
      pdf.line(x, y, x + element.size.width, y + element.size.height);
      break;
    case 'triangle':
      pdf.triangle(
        x + element.size.width / 2, y,
        x, y + element.size.height,
        x + element.size.width, y + element.size.height,
        style
      );
      break;
  }
};

const renderImageElement = async (
  pdf: jsPDF,
  element: ImageElement,
  offsetX: number,
  offsetY: number
): Promise<void> => {
  if (!element.src) return;
  
  try {
    pdf.addImage(
      element.src,
      'PNG',
      offsetX + element.position.x,
      offsetY + element.position.y,
      element.size.width,
      element.size.height
    );
  } catch (error) {
    console.error('Failed to render image:', error);
  }
};

const getIconComponent = (iconName: string) => {
  const pascalCase = iconName
    .split('-')
    .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
    .join('');
  return LucideIcons[pascalCase as keyof typeof LucideIcons];
};

const renderIconElement = async (
  pdf: jsPDF,
  element: IconElement,
  offsetX: number,
  offsetY: number
): Promise<void> => {
  try {
    const IconComponent = getIconComponent(element.iconName);
    if (!IconComponent) {
      console.error('Icon not found:', element.iconName);
      return;
    }

    // Render icon to SVG string
    const size = Math.min(element.size.width, element.size.height);
    const svgMarkup = renderToStaticMarkup(
      React.createElement(IconComponent, {
        width: 24,
        height: 24,
        color: element.color || '#000000',
        strokeWidth: 2,
      })
    );
    
    // Create an image from the SVG
    const svgBlob = new Blob([svgMarkup], { type: 'image/svg+xml' });
    const url = URL.createObjectURL(svgBlob);
    
    // Create canvas and draw SVG
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();
    
    await new Promise<void>((resolve, reject) => {
      img.onload = () => {
        // High resolution for print
        const scale = 4;
        canvas.width = 24 * scale;
        canvas.height = 24 * scale;
        ctx?.scale(scale, scale);
        ctx?.drawImage(img, 0, 0, 24, 24);
        resolve();
      };
      img.onerror = reject;
      img.src = url;
    });
    
    URL.revokeObjectURL(url);
    
    const imgData = canvas.toDataURL('image/png');
    
    // Center the icon in the element
    const xOffset = (element.size.width - size) / 2;
    const yOffset = (element.size.height - size) / 2;
    
    pdf.addImage(
      imgData,
      'PNG',
      offsetX + element.position.x + xOffset,
      offsetY + element.position.y + yOffset,
      size,
      size
    );
  } catch (error) {
    console.error('Failed to render icon:', error);
  }
};
